/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

interface CalculateProps {
  value: string | number | undefined
  decimalPlaces?: number
  defaultRem?: number
  maxLenth?: number
}

export const calculateFontsize = (data: CalculateProps) => {

  const { value, decimalPlaces = 2, defaultRem = 1, maxLenth = 10 } = data;

  if (!value) {
    return defaultRem
  }

  const [n = [], decimals = []] = String(value).split('.');
  const totalSpace = n.length + Math.min(decimals.length, decimalPlaces);
  return totalSpace > maxLenth ? defaultRem * maxLenth / totalSpace : defaultRem;
};

export const onlyLettersAndNumbers = (str: string) => {
  return /^[A-Za-z0-9]*$/.test(str);
};

export const isNumericString = (s: unknown): s is number => {
  if (typeof s === 'number') {
    return true;
  }

  if (typeof s !== 'string' || !s) {
    return false; // Empty string skips
  }
  return !Number.isNaN(Number(s));
};

export const truncate = (text: string, n: number, useWordBoundary: boolean = false): string => {
  if (text.length <= n) {
    return text;
  }
  const subString = text.slice(0, n);
  return (useWordBoundary ? subString.slice(0, subString.lastIndexOf(' ')) : subString) + '...';
};

export type SnakeToCamel<S extends string> = S extends `${infer Start}_${infer Rest}` ? `${Start}${Capitalize<SnakeToCamel<Rest>>}` : S

export function capitalize<S extends string>(string: S): Capitalize<S> {
  if (string.length === 0) {
    return '' as never
  }

  return (string[0].toUpperCase() + string.slice(1)) as never
}

export const compareStringArrays = <T extends string>({
  oldArray,
  newArray,
}: {
  oldArray: T[];
  newArray: T[];
}): { added: T[]; removed: T[] } => {
  const added = newArray.filter((item) => !oldArray.includes(item));
  const removed = oldArray.filter((item) => !newArray.includes(item));

  return { added, removed };
};

export const isValidObjectId = (id: string | undefined | null): id is string => {
  if (!id) {
    return false;
  }
  return (/^[0-9a-fA-F]{24}$/).test(id);
};

// Validation constants - must match backend validation (api.g17.eco/server/models/valueList.ts)
export const CODE_REGEX = /^[a-z0-9/-]+$/;
export const CODE_REGEX_ERROR =
  'Code can only contain lowercase alphanumeric, dash(-) and forward slash(/)';